// SelectorCampanas.tsx
//
// Primer paso del módulo: las campañas que ya existen. Al elegir una se ve su
// estado —consulta, envío y detalle de contactos— sin volver a cargar el
// archivo. La otra vía es cargar un archivo nuevo, que pasa a ser la campaña
// en pantalla y de ahí en adelante avanza paso a paso.

import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  List,
  ListItemButton,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import ClearIcon from "@mui/icons-material/Close";
import { ESTADO_LOTE_LABEL, estadoColor } from "../hooks/useFidelizacionCampana";
import { pctConsulta, pctEnvio } from "./ProgresoFidelizacion";
import type { FidelizacionLote } from "../fidelizacion-campana.interface";

interface Props {
  lotes: FidelizacionLote[];
  seleccionado: number | null;
  onSelect: (loteId: number) => void;
  cargando?: boolean;
  total: number;
  pagina: number;
  tamanoPagina: number;
  onPaginaChange: (pagina: number) => void;
  onTamanoChange: (tamano: number) => void;
  onFiltrosChange: (filtros: { fechaDesde?: string; fechaHasta?: string }) => void;
}

///* atajos de rango. "Todas" deja los campos vacios y el backend sin filtro
const RANGOS = [
  { etiqueta: "Todas", dias: 0 },
  { etiqueta: "Hoy", dias: 1 },
  { etiqueta: "7 días", dias: 7 },
  { etiqueta: "30 días", dias: 30 },
];

const yyyyMmDd = (fecha: Date) => fecha.toISOString().slice(0, 10);

const diasAtras = (dias: number) => {
  const hoy = new Date();
  hoy.setDate(hoy.getDate() - (dias - 1));
  return yyyyMmDd(hoy);
};

const RANGO_ETIQUETAS: Record<string, number> = Object.fromEntries(
  RANGOS.map((r) => [r.etiqueta, r.dias]),
);

///* una de las dos fases del panel, en miniatura
const MiniFase = ({ label, valor }: { label: string; valor: number }) => (
  <Box sx={{ flex: "1 1 90px", minWidth: 90 }}>
    <Stack direction="row" justifyContent="space-between" spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {`${valor}%`}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={valor}
      color={valor >= 100 ? "success" : "info"}
      sx={{ height: 4, borderRadius: 2 }}
    />
  </Box>
);

const fechaCorta = (fecha?: string) => {
  if (!fecha) return "sin fecha";
  const dia = new Date(fecha);
  return Number.isNaN(dia.getTime())
    ? "sin fecha"
    : dia.toLocaleString("es-EC", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export const SelectorCampanas = ({
  lotes,
  seleccionado,
  onSelect,
  cargando,
  total,
  pagina,
  tamanoPagina,
  onPaginaChange,
  onTamanoChange,
  onFiltrosChange,
}: Props) => {
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const totalPaginas = Math.max(1, Math.ceil(total / tamanoPagina));

  ///* se aplica al soltar el rango, no en cada tecla: si no, cada fecha escrita
  ///* dispara una consulta nueva
  const aplicarRango = (etiqueta: string) => {
    const dias = RANGO_ETIQUETAS[etiqueta] ?? 0;
    const desde = dias > 0 ? diasAtras(dias) : "";
    setFechaDesde(desde);
    setFechaHasta("");
    onFiltrosChange({ fechaDesde: desde || undefined, fechaHasta: undefined });
  };

  const aplicarFechas = (desde: string, hasta: string) => {
    onFiltrosChange({
      fechaDesde: desde || undefined,
      fechaHasta: hasta || undefined,
    });
  };

  const limpiar = () => {
    setFechaDesde("");
    setFechaHasta("");
    onFiltrosChange({});
  };
  ///* se muestran de la más reciente a la más antigua
  const ordenadas = useMemo(
    () => [...lotes].sort((a, b) => b.id - a.id),
    [lotes],
  );

  const hayFiltro = Boolean(fechaDesde || fechaHasta);

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {RANGOS.map((rango) => (
          <Button
            key={rango.etiqueta}
            size="small"
            variant={
              ((RANGO_ETIQUETAS[rango.etiqueta] ?? 0) > 0 && fechaDesde === diasAtras(RANGO_ETIQUETAS[rango.etiqueta]!) && !fechaHasta) ||
              (rango.dias === 0 && !hayFiltro)
                ? "contained"
                : "outlined"
            }
            onClick={() => aplicarRango(rango.etiqueta)}
          >
            {rango.etiqueta}
          </Button>
        ))}

        <TextField
          type="date"
          size="small"
          label="Desde"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          onBlur={(e) => aplicarFechas(e.target.value, fechaHasta)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 155 }}
        />
        <TextField
          type="date"
          size="small"
          label="Hasta"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          onBlur={(e) => aplicarFechas(fechaDesde, e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 155 }}
        />
        {hayFiltro && (
          <Button size="small" startIcon={<ClearIcon />} onClick={limpiar}>
            Limpiar
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <TextField
          select
          size="small"
          label="Por página"
          value={tamanoPagina}
          onChange={(e) => onTamanoChange(Number(e.target.value))}
          sx={{ width: 118 }}
        >
          {[5, 10, 20].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {cargando && !ordenadas.length ? (
        <Stack spacing={1}>
          {[0, 1, 2].map((fila) => (
            <Skeleton key={fila} variant="rounded" height={62} />
          ))}
        </Stack>
      ) : !ordenadas.length ? (
        <Typography variant="body2" color="text.secondary">
          {hayFiltro
            ? "No hay campañas en ese rango de fechas."
            : "Todavía no hay campañas. Carga un archivo para empezar."}
        </Typography>
      ) : (
        <List disablePadding>
      {ordenadas.map((lote) => {
        const activo = seleccionado === lote.id;
        return (
          <ListItemButton
            key={lote.id}
            selected={activo}
            onClick={() => onSelect(lote.id)}
            aria-current={activo}
            sx={{
              alignItems: "flex-start",
              gap: 1.25,
              py: 1.25,
              mb: 0.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: activo ? "primary.main" : "divider",
            }}
          >
            <Box sx={{ pt: 0.25, color: activo ? "primary.main" : "text.secondary" }}>
              <CampaignIcon fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 320 }}>
                  {lote.nombre_archivo}
                </Typography>
                <Chip
                  size="small"
                  label={ESTADO_LOTE_LABEL[lote.estado] ?? lote.estado}
                  color={estadoColor(lote.estado)}
                />
              </Stack>

              <Typography variant="caption" color="text.secondary" component="p" noWrap>
                {`#${lote.id} · ${fechaCorta(lote.fecha_carga)} · ${lote.total_filas} filas · ${
                  lote.contactables
                } contactables · ${lote.enviados} enviados`}
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
                <MiniFase label="Consulta" valor={pctConsulta(lote)} />
                <MiniFase label="Envío" valor={pctEnvio(lote)} />
              </Stack>
            </Box>
          </ListItemButton>
        );
      })}
        </List>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          {hayFiltro
            ? `${total} campaña${total === 1 ? "" : "ñ"}a${total === 1 ? "" : "s"} en el rango`
            : `${total} campaña${total === 1 ? "" : "ñ"}a${total === 1 ? "" : "s"} en total`}
        </Typography>
        {totalPaginas > 1 && (
          <Pagination
            size="small"
            color="primary"
            page={pagina + 1}
            count={totalPaginas}
            onChange={(_, valor) => onPaginaChange(valor - 1)}
            siblingCount={0}
            boundaryCount={1}
          />
        )}
      </Stack>
    </Stack>
  );
};
